/**
 * What taking the next rank of a perk would actually do.
 *
 * Computed by building the character twice and subtracting, rather than by reading the perk's own
 * numbers. That is the whole point: a perk's authored `perRank` is not what a player will feel,
 * because levers combine multiplicatively with everything else already taken and every field
 * clamps at its bounds. A screen that printed the authored number would be lying to anybody who
 * had taken the perk twice already, and lying loudest to whoever had hit a cap.
 *
 * The moment is a parameter with a resting default, so a conditional perk previews as what it is
 * worth while its condition holds. Showing a reload perk as "no change" because the character is
 * standing still on a menu would be true and useless.
 */

import { STAT_ORDER } from '@/sim/combat/statContract';
import { EFFECT_FIELD_ORDER, effectField } from '@/sim/effects/effectFieldContract';
import { RESTING_MOMENT } from '@/sim/effects/characterMoment';
import { characterBuild } from '@/sim/progression/characterBuild';
import { conditionHolds, effectCondition } from '@/sim/effects/effectConditionRegistry';
import { perk } from '@/sim/progression/perkRegistry';
import { rankOf } from '@/sim/progression/progressState';
import type { CharacterMoment } from '@/sim/effects/characterMoment';
import type { CharacterSheet } from '@/sim/progression/characterSheet';
import type { EffectFieldId } from '@/sim/effects/effectFieldContract';
import type { Equipment } from '@/sim/inventory/inventoryContract';
import type { EffectConditionId } from '@/sim/effects/effectConditionContract';
import type { PerkId } from '@/sim/progression/perkContract';

/** One line of a before and after. `better` is the field's own answer, never the sign. */
export interface PreviewChange {
  readonly label: string;
  readonly before: number;
  readonly after: number;
  readonly better: boolean;
  /** The condition this only applies under, spelled out, or empty when it always applies. */
  readonly when: string;
}

/**
 * Every number one more rank would move, in a fixed order: stats first, then levers.
 *
 * Empty when nothing would change, which is a real answer rather than a missing one: a perk at max
 * rank moves nothing, and so does one whose every lever is already pinned at its bound.
 */
export function previewPerk(
  sheet: CharacterSheet,
  equipment: Equipment,
  id: PerkId,
  moment: CharacterMoment = RESTING_MOMENT,
): readonly PreviewChange[] {
  const def = perk(id);
  const rank = rankOf(sheet.progress, id);

  if (rank >= def.maxRanks) return [];

  // Previewed under a moment where the perk's own conditions hold, so a conditional perk shows
  // what it is for rather than what it is worth standing still in a menu.
  const under = momentFor(
    def.effects.map((effect) => effect.condition),
    moment,
  );
  const before = characterBuild(sheet, equipment, under);
  const after = characterBuild(
    {
      ...sheet,
      progress: { ...sheet.progress, perkRanks: { ...sheet.progress.perkRanks, [id]: rank + 1 } },
    },
    equipment,
    under,
  );

  const changes: PreviewChange[] = [];

  for (const stat of STAT_ORDER) {
    if (before.stats[stat] === after.stats[stat]) continue;
    changes.push({
      label: stat,
      before: before.stats[stat],
      after: after.stats[stat],
      better: after.stats[stat] > before.stats[stat],
      when: '',
    });
  }

  for (const field of EFFECT_FIELD_ORDER) {
    if (before.effects[field] === after.effects[field]) continue;
    changes.push({
      label: effectField(field).label,
      before: before.effects[field],
      after: after.effects[field],
      better: isBetter(field, before.effects[field], after.effects[field]),
      when: whenOf(def.effects.find((effect) => effect.field === field)?.condition ?? null),
    });
  }

  if (after.loadout.damageModifiers.length > before.loadout.damageModifiers.length) {
    changes.push({
      label: 'Damage dealt',
      before: 1,
      after: addedScale(before, after),
      better: true,
      when: '',
    });
  }

  return changes;
}

function isBetter(field: EffectFieldId, before: number, after: number): boolean {
  return effectField(field).betterWhen === 'higher' ? after > before : after < before;
}

/** The added modifier's own scale, so the row prints what this rank is worth and not the stack. */
function addedScale(
  before: ReturnType<typeof characterBuild>,
  after: ReturnType<typeof characterBuild>,
): number {
  const added = after.loadout.damageModifiers.slice(before.loadout.damageModifiers.length);

  return added.reduce((total, modifier) => total * modifier.scale, 1);
}

/**
 * A moment in which as many of these conditions hold as can hold at once.
 *
 * Searched rather than constructed: each candidate is a moment a body can actually be in, and the
 * first that satisfies every condition the perk names wins. That is a walk over three entries and
 * it means a condition added later needs no case here, only an entry, and a perk whose conditions
 * contradict each other previews under the resting moment rather than under an impossible body.
 */
function momentFor(
  conditions: readonly (EffectConditionId | null)[],
  base: CharacterMoment,
): CharacterMoment {
  const wanted = conditions.filter(
    (condition): condition is EffectConditionId => condition !== null,
  );

  if (wanted.length === 0) return base;

  const candidates: readonly CharacterMoment[] = [
    { ...base, moving: true, downed: false },
    { ...base, moving: true, downed: false, healthFraction: 0 },
    { ...base, downed: true },
    { ...base, moving: true, downed: true },
  ];

  return (
    candidates.find((candidate) => wanted.every((id) => conditionHolds(id, candidate))) ?? base
  );
}

/** What a row says about when it applies, in the condition's own words. */
function whenOf(condition: EffectConditionId | null): string {
  return condition === null ? '' : effectCondition(condition).label;
}
